import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import css from './ListingFieldRichText.module.css';
import IconCollection from '../IconCollection/IconCollection';
import { Button, Chevron } from '../../components';
import FieldCheckbox from '../FieldCheckbox/FieldCheckbox';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import ToolTip from '../ToolTip/ToolTip';

const modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['blockquote'],
        ['link'],
        ['clean'],
    ],
};

const formats = [
    'bold', 'italic', 'underline',
    'header',
    'list', 'bullet',
    'align',
    'blockquote',
    'link', 
];

const ListingFieldRichText = ({
    name,
    sectionTitle,
    label,
    value = '',
    onChange,
    placeholder,
    helperText,
    required,
    maxLength = 2000,
    showRegenerate = true,
    onRegenerate,
    regenerateDisabled,
    showCredits = true,
    creditsUsed,
    maxCredits,
    onCreditsInfoClick,
    showChevron = true,
    showToggle = false,
    toggleValue,
    enabled,
    validate,
    generateInProgress,
    generateError,
    ...rest
}) => {
    const [expanded, setExpanded] = useState(false);
    const [ReactQuill, setReactQuill] = useState(null);
    const quillRef = useRef();
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 767);
    const charCount = value ? value.replace(/<[^>]+>/g, '').length : 0;

    // Dynamically import ReactQuill only on client
    useEffect(() => {
        if (typeof window !== 'undefined') {
            import('react-quill').then((mod) => {
                setReactQuill(() => mod.default);
                import('react-quill/dist/quill.snow.css');
            });
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(window.innerWidth <= 767);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const hiddenClass = !expanded ? css.bodyCollapsed : '';
    return (
        <div className={css.root}>
            <div className={css.header}>
                <div className={css.headerTitle}>
                    <h1 className={css.sectionTitle}>{sectionTitle}</h1>

                </div>

                <div className={css.headerActions}>
                    {!isMobile && showRegenerate && (
                        <Button
                            variant="warning"
                            type="button"
                            size={"sm"}
                            className={classNames(css.regenerateBtn, hiddenClass)}
                            onClick={onRegenerate}
                            disabled={regenerateDisabled}
                        >
                            <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                                <IconCollection name="icon_regenrate" width={16} height={16} />
                            </span> Regenerate
                        </Button>
                    )}
                    {showToggle && (
                        <div className={css.toggleWrapper}>
                            <label className={css.toggleLabel}>
                                <FieldCheckbox
                                    id={`${name}Enabled`}
                                    name={`${name}Enabled`}
                                    value={toggleValue}
                                    label=""
                                    className={css.toggleInput}
                                />
                                <span className={classNames(css.toggleSlider, { [css.enabled]: enabled })}></span>
                            </label>
                            <span className={css.toggleLabelText}>Enabled</span>
                        </div>
                    )}

                    {showChevron && (
                        <button
                            type="button"
                            className={css.toggleBtn}
                            onClick={() => setExpanded(e => !e)}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                            <Chevron expanded={expanded} width={24} height={24} />
                        </button>
                    )}
                </div>
            </div>
            <span className={css.label}>{label}{required && <span className={css.required}>*</span>}</span>
            <div className={expanded ? css.body : css.bodyCollapsed}>
                <div className={css.inputRow}>
                    {ReactQuill ? (
                        <Field name={name} validate={validate}>
                            {({ input, meta }) => {
                                return (
                                    <div className={css.richTextField}>
                                        <ReactQuill
                                            ref={quillRef}
                                            className={css.richText}
                                            theme="snow"
                                            value={input.value}
                                            onChange={input.onChange}
                                            placeholder={placeholder}
                                            modules={modules}
                                            formats={formats}
                                            onBlur={input.onBlur}
                                            
                                        />
                                        {meta.error && meta.touched && <div className={css.error}>{meta.error}</div>}
                                    </div>
                                )
                            }}
                        </Field>
                    ) : (
                        <div style={{ minHeight: 180, width: '100%' }} />
                    )}
                    <span className={css.charCounter}>{charCount}/{maxLength}</span>
                </div>
                {!!helperText && <div className={css.helperText}>{helperText}</div>}
                <div className={classNames(css.mobileActions, hiddenClass)}>
                    {isMobile && showRegenerate && (
                        <Button
                            variant="warning"
                            type="button"
                            size={"sm"}
                            className={classNames(css.regenerateBtn, hiddenClass)}
                            onClick={onRegenerate}
                            disabled={regenerateDisabled}
                        >
                            <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                                <IconCollection name="icon_regenrate" width={16} height={16} />
                            </span> Regenerate
                        </Button>
                    )}
                    {showCredits && <div className={css.creditsRow}>
                        <span className={css.creditsText}>{creditsUsed}/{maxCredits} credits used</span>
                        {/* {typeof onCreditsInfoClick === 'function' && (
                            <button type="button" className={css.creditsInfoBtn} onClick={onCreditsInfoClick}>
                                <IconCollection name="info_icon" width={16} height={16} />
                            </button>
                        )} */}
                         <ToolTip content={`${creditsUsed}/${maxCredits} credits used`}>
                            <button type="button" className={css.creditsInfoBtn} >
                                <IconCollection name="info_icon" width={16} height={16} />
                            </button>
                        </ToolTip>
                    </div>}
                </div>
            </div>
        </div>
    );
};

ListingFieldRichText.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    showRegenerate: PropTypes.bool,
    onRegenerate: PropTypes.func,
    regenerateDisabled: PropTypes.bool,
    showCredits: PropTypes.bool,
    creditsUsed: PropTypes.number,
    maxCredits: PropTypes.number,
    onCreditsInfoClick: PropTypes.func,
    showChevron: PropTypes.bool,
    showToggle: PropTypes.bool,
    toggleValue: PropTypes.string,
    enabled: PropTypes.bool,
    validate: PropTypes.func,
};

export default ListingFieldRichText;
